import React, { useState, useEffect, useRef } from 'react';
import { bindActionCreators } from 'redux';
import { connect, useSelector } from 'react-redux';
import { useStore } from 'react-redux';

// Configs

import { configs } from '@constants/configs/configs';
import { attestationsConfig } from '@components/ssap/pages/Page3_17';

// Components

import AttestationsInfo from './AttestationsInfo';
import FlagsTooltip from './FlagsTooltip';
import PagesTooltip from './PagesTooltip';
import Taxes from './Taxes';
import IdsInfo from './IdsInfo';
import BloodRelationshipsInfo from './BloodRelationshipsInfo';
import CurrentJsonExtractor from './CurrentJsonExtractor';
import NewJsonSetter from './NewJsonSetter';
import ReduxStoreInfo from './ReduxStoreInfo';
import EscPageRenderInfo from './EscPageRenderInfo';

// ActionCreators

import { changeData, changePage } from '@actions/ssap/ssapActionCreators';

// Selectors

import {
    selectIfInlineEligibilityIgnoredForEscPage,
    selectIsInlineCurrentIncomeEnabled
} from '@reducers/appReducer';

// Utils

import { localStorageHyperDashboardTools } from '@utils/tools/localStorageHyperDashboardTools';


const Manager = props => {

    const {
        actions: {
            changePage
        },
        adjustedFlow,
        bloodRelationshipCodes,
        isContentSkipped,
        buttons,
        data,
        data: {
            ssapApplicationId,
            taxHousehold: {
                bloodRelationship,
                primaryTaxFilerPersonId
            }
        },
        householdMembers,
        page,
        relations: {
            loadedRelations
        },
        environment: {
            localDates: {
                serverDate
            },
            stateCode,
            isState
        },
        flow: {
            flowType
        }
    } = props;

    const storeData = useStore().getState();

    const inlineEligibilityIgnoredForEscPage = useSelector(selectIfInlineEligibilityIgnoredForEscPage);
    const inlineCurrentIncomeEnabled = useSelector(selectIsInlineCurrentIncomeEnabled);

    const [ isChecking, toggleChecking ] = useState(false);
    const [ pageForCheck, getNextPage ] = useState(0);

    const openedItem = localStorageHyperDashboardTools.getValueByPropName('preOpenedMenuItem', 'default');

    const [ state, setState ] = useState({
        ...buttons,
        ...(openedItem && openedItem !== 'default' ? { [openedItem]: true } : {} )
    });

    const {
        TaxHouseholdComposition,
        Pages,
        Flags,
        Identifiers,
        JsonSetter,
        JsonGetter,
        Relations,
        Attestations,
        ReduxStore,
        EscPageRender
    } = state;

    const defaultButtonStyle = {
        padding: '3px',
        backgroundColor: '#00a6d2',
        outline: 'none',
        borderRadius: '3px',
        cursor: 'pointer',
        marginRight: '20px',
        color: 'black'
    };



    const handleToggle = ({ target: { name } }) => {
        setState(state => Object
            .keys(state)
            .reduce((newState, btn) => btn === name ? { ...newState, [name]: !state[name] } : { ...newState, [btn]: false }, {}));
    };

    useEffect(() => {
        const tooltipsCloseHandlers = e => {
            if(Object.keys(state).some(key => key === e.target.id)) {
                handleToggle({ target: { name: e.target.id } });
            }
        };
        window.addEventListener('click', tooltipsCloseHandlers);

        return () => window.removeEventListener('click', tooltipsCloseHandlers);
    }, []);

    const [ json, changeJson ] = useState('');
    const [ isApplyJsonButtonDisabled, toggleApplyJsonButtonDisability ] = useState(true);

    const refToJsonTextArea = useRef();

    const getButtons = () => {
        return Object.keys(state).map(btnName => {
            return (
                <button
                    key={btnName}
                    name={btnName}
                    onClick={ handleToggle }
                    style={{
                        ...defaultButtonStyle,
                        backgroundColor: state[btnName] ? 'rgb(134, 196, 140)' : '#00a6d2'
                    }}
                >
                    {btnName}
                </button>
            );
        });
    };

    useEffect(() => {
        if(isChecking) {
            setTimeout(() => {
                changePage(pageForCheck);
                getNextPage(p => p + 1 <= adjustedFlow.length - 1 ? p + 1 : 0);
            }, 1000);
        }
    }, [ isChecking, pageForCheck ]);

    const handlePageCheck = () => {
        toggleChecking(isChecking => !isChecking);
    };

    return (
        <div 
            style={{ 
                padding: 0, 
                margin: '20px 20px', 
                position: 'relative',
                display: isContentSkipped ? 'inline' : 'block'
            }}
        >
            {
                !isContentSkipped &&

                <span
                    style={{ fontSize: '1.5rem', fontWeight: 'bold' }}
                >
                    Household info
                </span>
            }

            <div
                style={{
                    fontWeight: 'bold',
                    fontSize: '1.5rem',
                    padding: '7px 0px',
                    display: 'inline-block',
                    marginLeft: '10px',
                    position: 'absolute',
                    left: '200px',
                    top: '-1.5rem'
                }}
            >
                {
                    getButtons()
                }

                { TaxHouseholdComposition && <Taxes id={'TaxHouseholdComposition'} /> }
                { Flags && <FlagsTooltip id={'Flags'} /> }
                { Pages && <PagesTooltip { ...{ adjustedFlow, id: 'Pages', page, changePage } } /> }
                { Identifiers && <IdsInfo { ...{ id: 'Identifiers', data, ssapApplicationId } } /> }

                {
                    JsonSetter && <NewJsonSetter {...{
                        id: 'JsonSetter',
                        name: JsonSetter,
                        btnText: 'Apply SSAP JSON',
                        appType: configs.routes.SSAP,
                        json,
                        changeJson,
                        isApplyJsonButtonDisabled,
                        toggleApplyJsonButtonDisability,
                        handleToggle,
                        changeData: props.actions.changeData
                    }} />
                }

                {
                    JsonGetter && <CurrentJsonExtractor {...{
                        id: 'JsonGetter',
                        name: JsonGetter,
                        buttons:[
                            ['Copy SSAP JSON as "taxHousehold" === [{ }]', 'array'],
                            ['Copy SSAP JSON as "taxHousehold" === {}', 'object'],
                            ['Copy SSAP JSON for mock file', 'array', true]
                        ],
                        data,
                        refToJsonTextArea,
                        handleToggle
                    }} />
                }

                {
                    Relations && <BloodRelationshipsInfo { ...{
                        id: 'Relations',
                        bloodRelationship,
                        householdMembers,
                        loadedRelations
                    }} />
                }

                {
                    Attestations && <AttestationsInfo { ...{
                        id: 'Attestations',
                        attestationsConfig,
                        flowType,
                        stateCode
                    } } />
                }

                {
                    EscPageRender && <EscPageRenderInfo { ...{
                        id: 'EscPageRender',
                        bloodRelationship,
                        bloodRelationshipCodes,
                        inlineEligibilityIgnoredForEscPage,
                        inlineCurrentIncomeEnabled,
                        isState,
                        householdMembers,
                        loadedRelations,
                        primaryTaxFilerPersonId,
                        serverDate
                    } } />
                }

                {
                    ReduxStore && 
                        
                    <ReduxStoreInfo
                        { ...{ 
                            id: 'ReduxStore',
                            storeData
                        } }
                    />
                }

            </div>

            {
                !isContentSkipped &&

                <button
                    style={{
                        backgroundColor: isChecking ? 'red': 'green',
                        borderRadius: '7px',
                        position: 'absolute',
                        padding: '3px',
                        top: '-5px',
                        cursor: 'pointer'
                    }}
                    onClick={handlePageCheck}
                >
                    {`Checker ${isChecking ? 'Off' : 'On'}`}
                </button>

            }
       
        </div>
    );
};

const mapStateToProps = state => ({
    environment: state.common.environment,

    adjustedFlow: state.ssap.adjustedFlow,
    bloodRelationshipCodes: state.ssap.relations.codes,
    data: state.ssap.data,
    flow: state.ssap.flow,

    householdMembers: state.ssap
        .data
        .taxHousehold
        .householdMember,

    page: state.ssap.page,
    relations: state.ssap.relations
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        changeData,
        changePage
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Manager);